import { useState, useEffect } from 'react';
import firebase from 'firebase/app';

/**
 * One Firestore listener per team, shared by everything on screen.
 *
 * The access guard and the team page both need the team document, and each used
 * to open its own subscription — two listeners on the same doc, two chances to
 * disagree about its state. Every screen that gains a guard would have added
 * another. This keeps a single listener per teamId, reference-counted, and hands
 * the cached value to anything that subscribes later so a second consumer costs
 * nothing and resolves immediately.
 */
const subscriptions = new Map();

export function subscribeToTeamDoc(teamId, listener) {
  let entry = subscriptions.get(teamId);

  if (!entry) {
    entry = { listeners: new Set(), team: null, loaded: false, error: null, unsubscribe: null };
    subscriptions.set(teamId, entry);

    const publish = () => entry.listeners.forEach((notify) => notify(entry));

    entry.unsubscribe = firebase.firestore().collection('teams').doc(teamId).onSnapshot(
      (doc) => {
        // Spread of a missing doc yields { id, teamId } — the shape callers have
        // always seen for a team that isn't there.
        entry.team = { ...doc.data(), id: doc.id, teamId: doc.id };
        entry.loaded = true;
        entry.error = null;
        publish();
      },
      (error) => {
        console.error(`Team subscription failed for ${teamId}:`, error);
        entry.error = error;
        entry.loaded = true;
        publish();
      },
    );
  }

  entry.listeners.add(listener);
  if (entry.loaded) listener(entry);

  return () => {
    entry.listeners.delete(listener);
    if (entry.listeners.size === 0) {
      if (entry.unsubscribe) entry.unsubscribe();
      subscriptions.delete(teamId);
    }
  };
}

/**
 * @returns {{team: object|null, loaded: boolean, error: Error|null}}
 *   `loaded` distinguishes "not answered yet" from "answered, and there is no
 *   team" — the two look identical if you only have the value.
 */
export function useTeamDoc(teamId) {
  const [state, setState] = useState({ team: null, loaded: false, error: null });

  useEffect(() => {
    if (!teamId) {
      setState({ team: null, loaded: true, error: null });
      return undefined;
    }

    setState({ team: null, loaded: false, error: null });
    return subscribeToTeamDoc(teamId, (entry) => {
      setState({ team: entry.team, loaded: entry.loaded, error: entry.error });
    });
  }, [teamId]);

  return state;
}
